"use client";

import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { useMutation } from "@tanstack/react-query";
import { signIn } from "next-auth/react";
import { FcGoogle } from "react-icons/fc";
import Logo from "@/components/logo";

const loginSchema = yup.object({
  email: yup.string().email("Invalid email").required("Email is required"),
});

type LoginFormData = yup.InferType<typeof loginSchema>;

interface LoginModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function LoginModal({ open, onOpenChange }: LoginModalProps) {
  const { toast } = useToast();

  const form = useForm<LoginFormData>({
    resolver: yupResolver(loginSchema),
    defaultValues: { email: "" },
  });

  const emailSignIn = useMutation({
    mutationFn: async (email: string) => {
      const result = await signIn("email", { email });
      if (result?.error) throw new Error(result.error);
      return result;
    },
    onSuccess: () => {
      toast({
        title: "Check your email",
        description: "A sign in link has been sent to your email address.",
      });
      form.reset();
      onOpenChange(false);
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const handleGoogleSignIn = () => signIn("google");
  const onSubmit = (data: LoginFormData) => emailSignIn.mutate(data.email);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[420px] rounded-2xl border border-[#EEF3FF] dark:border-[#1B2845] bg-white dark:bg-[#111827] p-8 shadow-xl shadow-black/5">
        <DialogTitle className="sr-only">Sign in to AuthorFlows</DialogTitle>

        {/* Logo */}
        <div className="flex justify-center mb-6">
          <Logo path="/" />
        </div>

        {/* Label */}
        <div className="flex items-center justify-center gap-3 mb-5">
          <span className="block h-px w-5 bg-[#3A72E8]/40" />
          <span className="text-[11px] font-medium text-[#999999] dark:text-[#485E80] tracking-[0.18em] uppercase">
            Sign in
          </span>
          <span className="block h-px w-5 bg-[#3A72E8]/40" />
        </div>

        {/* Heading */}
        <div className="text-center mb-8">
          <h2 className="text-3xl font-bold font-caveat text-[#1A1A1A] dark:text-[#E8EEFF] mb-2">
            Welcome back.
          </h2>
          <p className="text-[14px] text-[#555555] dark:text-[#90A6C8] leading-relaxed">
            Sign in to continue writing your story.
          </p>
        </div>

        {/* Google */}
        <button
          type="button"
          onClick={handleGoogleSignIn}
          className="flex items-center justify-center gap-3 w-full h-11 rounded-full border border-[#EEF3FF] dark:border-[#1B2845] bg-white dark:bg-[#111827] text-[14px] font-medium text-[#1A1A1A] dark:text-[#E8EEFF] hover:bg-[#F8FAFF] dark:hover:bg-[#0F1525] transition-colors mb-6"
        >
          <FcGoogle className="h-5 w-5" />
          Continue with Google
        </button>

        {/* Divider */}
        <div className="relative mb-6">
          <div className="absolute inset-0 flex items-center">
            <span className="w-full border-t border-[#EEF3FF] dark:border-[#1B2845]" />
          </div>
          <div className="relative flex justify-center">
            <span className="bg-white dark:bg-[#111827] px-3 text-[11px] text-[#999999] dark:text-[#485E80] tracking-wide uppercase">
              or email
            </span>
          </div>
        </div>

        {/* Email form */}
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-3">
          <Input
            {...form.register("email")}
            type="email"
            placeholder="your@email.com"
            disabled={emailSignIn.isPending}
            className="h-11 rounded-xl border-[#EEF3FF] dark:border-[#1B2845] bg-[#F8FAFF] dark:bg-[#0F1525] text-[14px] placeholder:text-[#AAAAAA] dark:text-[#E8EEFF] dark:placeholder:text-[#485E80]"
          />
          {form.formState.errors.email && (
            <p className="text-[12px] text-red-500">
              {form.formState.errors.email.message}
            </p>
          )}
          <Button
            type="submit"
            disabled={emailSignIn.isPending}
            className="w-full btn-glass-md"
          >
            {emailSignIn.isPending ? "Sending link…" : "Send magic link"}
          </Button>
        </form>

        <p className="text-center text-[11px] text-[#AAAAAA] dark:text-[#485E80] mt-6 leading-relaxed">
          By signing in, you agree to our{" "}
          <a href="/terms" className="text-[#3A72E8] dark:text-[#6B9BF5] hover:underline">
            Terms
          </a>{" "}
          and{" "}
          <a href="/privacy" className="text-[#3A72E8] dark:text-[#6B9BF5] hover:underline">
            Privacy Policy
          </a>
          .
        </p>
      </DialogContent>
    </Dialog>
  );
}
